import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { X, User, LogOut, Home, CreditCard, Shield } from 'lucide-react'
import Button from '../UI/Button'

const MobileMenu = ({ isOpen, onClose, navLinks, user, onSignOut, isAdmin }) => {
  const location = useLocation()

  // Close menu on route change
  useEffect(() => {
    onClose()
  }, [location.pathname])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const isActive = (href) => location.pathname === href

  const iconMap = {
    '/': Home,
    '/pricing': CreditCard,
  }

  return (
    <>
      {/* Overlay */}
      <div
        className={`
          fixed inset-0 z-50 bg-dark-950/80 backdrop-blur-sm
          transition-opacity duration-300
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        onClick={onClose}
      />

      {/* Menu Panel */}
      <div
        className={`
          fixed top-0 right-0 z-50
          w-full max-w-sm h-full
          bg-dark-900 border-l border-dark-700
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-700">
          <span className="text-lg font-semibold gradient-text">voxerface</span>
          <button
            onClick={onClose}
            className="p-2 text-dark-400 hover:text-dark-100 touch-target"
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="p-4 space-y-2">
          {navLinks.map((link) => {
            const Icon = iconMap[link.href] || Home
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl
                  transition-colors touch-target
                  ${isActive(link.href)
                    ? 'bg-accent-500/10 text-accent-400'
                    : 'text-dark-300 hover:bg-dark-800 hover:text-dark-100'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{link.label}</span>
              </Link>
            )
          })}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-dark-700 safe-area-bottom">
          {user ? (
            <div className="space-y-3">
              {isAdmin && (
                <Link to="/admin">
                  <Button variant="outline" fullWidth>
                    <Shield className="w-4 h-4" />
                    Admin Panel
                  </Button>
                </Link>
              )}
              <Link to="/dashboard">
                <Button variant="secondary" fullWidth>
                  <User className="w-4 h-4" />
                  Dashboard
                </Button>
              </Link>
              <Button
                variant="ghost"
                fullWidth
                onClick={() => {
                  onSignOut()
                  onClose()
                }}
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Link to="/auth">
                <Button variant="secondary" fullWidth>
                  Sign In
                </Button>
              </Link>
              <Link to="/auth?mode=signup">
                <Button variant="primary" fullWidth>
                  Get Started
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default MobileMenu
